import { type Component, createEffect, createMemo, createResource, createSignal, For, Show } from 'solid-js'
import { createStore } from 'solid-js/store'
import Button from '~/components/Button'
import Card from '~/components/Card'
import Chip from '~/components/Chip'
import Icon from '~/components/Icon'
import Select from '~/components/Select'
import { useToast } from '~/components/useToast'
import { COMPANY_ROLE_OPTIONS } from '~/services/access.service'
import { authService } from '~/services/auth.service'
import { companyService } from '~/services/company.service'
import { type CompanyRoleType, type UserCompanyType, type UserDetailType, userService } from '~/services/user.service'
import { resolveError } from '~/utils/error'

export interface UserCompaniesTabPropsType {
  /** La ficha del usuario cargada; null mientras llega. */
  user: () => UserDetailType | null | undefined
  /** El id del usuario editado. */
  userId: () => number
  /** Recarga la ficha tras guardar. */
  onRefetch: () => void
  /** true si el formulario es el usuario logueado. */
  isSelf: () => boolean
  /** Si quien edita puede asignar empresas. */
  canAssign: boolean
}

/** Pestaña de empresas: a cuáles entra el usuario, su rol y la que abre por defecto. */
const UserCompaniesTab: Component<UserCompaniesTabPropsType> = props => {
  const toast = useToast()

  /** Empresas donde quien edita puede meter a alguien (las suyas, o todas si es root). */
  const [availableCompanies] = createResource(async () => {
    try {
      const result = await companyService.list({ limit: 200 })
      return result.items
    } catch {
      return []
    }
  })

  // Con <For> cada tecleo recrearía la fila; el store escribe por ruta y la fila
  // sobrevive a la edición.
  const [memberships, setMemberships] = createStore<UserCompanyType[]>([])
  const [savingCompanies, setSavingCompanies] = createSignal(false)
  const [companyToAdd, setCompanyToAdd] = createSignal('')

  createEffect(() => {
    const data = props.user()
    if (data) setMemberships(data.companies.map(c => ({ ...c })))
  })

  const addableCompanies = createMemo(() => {
    const taken = new Set(memberships.map(m => m.companyId))
    return (availableCompanies() ?? []).filter(c => !taken.has(c.id)).map(c => ({ value: c.id, title: c.name, subtitle: c.slug ?? undefined }))
  })

  const addCompany = () => {
    const id = companyToAdd()
    if (!id) return
    const company = (availableCompanies() ?? []).find(c => c.id === id)
    if (!company) return
    setMemberships(memberships.length, {
      companyId: company.id,
      name: company.name,
      slug: company.slug ?? null,
      role: 'member',
      isDefault: memberships.length === 0,
      manageable: true
    })
    setCompanyToAdd('')
  }

  const removeCompany = (companyId: string) => {
    setMemberships(memberships.filter(m => m.companyId !== companyId))
  }

  /** Una sola empresa por defecto: marcar una desmarca la anterior. */
  const setDefaultCompany = (companyId: string) => {
    memberships.forEach((m, index) => {
      const shouldBeDefault = m.companyId === companyId
      if (m.isDefault !== shouldBeDefault) setMemberships(index, 'isDefault', shouldBeDefault)
    })
  }

  const saveCompanies = async () => {
    if (memberships.length === 0) {
      toast.addToast({ title: 'El usuario debe pertenecer al menos a una empresa', type: 'warning' })
      return
    }
    setSavingCompanies(true)
    try {
      const updated = await userService.syncCompanies(
        props.userId(),
        memberships.filter(m => m.manageable).map(m => ({ companyId: m.companyId, role: m.role, isDefault: m.isDefault }))
      )
      setMemberships(updated.map(c => ({ ...c })))
      toast.addToast({ title: 'Empresas actualizadas', type: 'success' })
      props.onRefetch()
      // Editando la ficha propia cambia la sesión en curso: hay que rehacer el
      // conmutador de empresas (y el token si la activa dejó de ser suya).
      if (props.isSelf()) await authService.me()
    } catch (err: unknown) {
      toast.addToast({ title: resolveError(err), type: 'danger' })
    } finally {
      setSavingCompanies(false)
    }
  }

  return (
    <Card>
      <Card.Title>
        <div class="flex items-center">
          <Icon icon="material-symbols-light:domain" size={20} class="mr-2" />
          <h2 class="text-lg font-semibold">Empresas del usuario</h2>
        </div>
      </Card.Title>
      <Card.Body>
        <p class="mb-4 text-sm text-foreground-subtle">Cada empresa lleva su propio rol. La marcada por defecto es la que se abre al iniciar sesión. Sólo se listan las empresas que tú administras.</p>

        <div class="space-y-3">
          <For
            each={memberships}
            fallback={<p class="rounded-lg border border-dashed border-border px-4 py-6 text-center text-sm text-foreground-subtle">Este usuario no pertenece a ninguna empresa</p>}
          >
            {(membership, index) => (
              <div class="flex flex-col gap-3 rounded-lg border border-border p-4 sm:flex-row sm:items-center">
                <div class="min-w-0 flex-1">
                  <div class="flex items-center gap-2">
                    <span class="truncate font-medium text-foreground">{membership.name}</span>
                    <Show when={membership.isDefault}>
                      <Chip size="xs" variant="soft" color="success" icon="material-symbols-light:star-outline">
                        Por defecto
                      </Chip>
                    </Show>
                    <Show when={!membership.manageable}>
                      <Chip size="xs" variant="soft" color="default">
                        Sólo lectura
                      </Chip>
                    </Show>
                  </div>
                  <span class="block truncate text-xs text-foreground-subtle">{membership.slug ?? membership.companyId}</span>
                </div>

                <div class="w-full sm:w-56">
                  <Select
                    label="Rol"
                    options={COMPANY_ROLE_OPTIONS}
                    value={membership.role}
                    disabled={!membership.manageable || !props.canAssign}
                    onChange={v => setMemberships(index(), 'role', String(v ?? 'member') as CompanyRoleType)}
                  />
                </div>

                <div class="flex items-center gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="soft"
                    color={membership.isDefault ? 'success' : 'default'}
                    tooltip="Marcar como empresa por defecto"
                    icon={membership.isDefault ? 'material-symbols-light:star' : 'material-symbols-light:star-outline'}
                    disabled={!membership.manageable}
                    onClick={() => setDefaultCompany(membership.companyId)}
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="soft"
                    color="danger"
                    tooltip="Quitar de esta empresa"
                    icon="material-symbols-light:delete-outline"
                    disabled={!membership.manageable || !props.canAssign}
                    onClick={() => removeCompany(membership.companyId)}
                  />
                </div>
              </div>
            )}
          </For>
        </div>

        <Show when={props.canAssign}>
          <div class="mt-6 flex flex-col gap-3 border-t border-border pt-4 sm:flex-row sm:items-end">
            <div class="flex-1">
              <Select
                label="Añadir a una empresa"
                options={addableCompanies()}
                value={companyToAdd()}
                placeholder={addableCompanies().length ? 'Selecciona una empresa...' : 'No hay más empresas disponibles'}
                clearable
                autocomplete
                disabled={addableCompanies().length === 0}
                onChange={v => setCompanyToAdd(String(v ?? ''))}
              />
            </div>
            <Button type="button" variant="soft" color="info" onClick={addCompany} disabled={!companyToAdd()} prependIcon="material-symbols-light:add-circle-outline">
              Añadir
            </Button>
          </div>
        </Show>

        <div class="mt-6 flex justify-end">
          <Button type="button" onClick={saveCompanies} disabled={savingCompanies() || !props.canAssign}>
            {savingCompanies() ? 'Guardando...' : 'Guardar empresas'}
          </Button>
        </div>
      </Card.Body>
    </Card>
  )
}

export default UserCompaniesTab
